import {colors, fonts, tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import type {ContentPlusShortBulletsSliceFragment} from '@/graphql-types'
import Image from '@helios/shared/components/image'
import RichText from '@helios/shared/components/rich-text'
import {Layout, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'
import mq from '@helios/shared/utils/media-query'

export const ContentPlusShortBulletsSlice = ({
    slice,
}: {
    slice: ContentPlusShortBulletsSliceFragment
}) => {
    const content = slice.primary

    return (
        <Layout
            as={Wrapper}
            variant="two-asymmetrical"
            alignItems="center"
            highlightColor={content.highlight_color}
            mirrored={content.inverse}
        >
            <RichText as="h2" variant="heading-large" content={content.heading.richText} />

            <ContentWrapper>
                {content.body && <RichText variant="title-base" content={content.body.richText} />}
                <BulletsGrid>
                    {hasNonEmptyItems(slice.items) &&
                        slice.items.map((item) => (
                            <BulletItem key={item.bullet_text}>
                                <BulletImage
                                    aria-hidden
                                    image={item.bullet_image}
                                    width={item.bullet_image.dimensions.width}
                                />
                                <Text>{item.bullet_text}</Text>
                            </BulletItem>
                        ))}
                </BulletsGrid>
            </ContentWrapper>
        </Layout>
    )
}

type WrapperProps = {
    highlightColor?: keyof typeof colors.background
}

const Wrapper = styled(Section)<WrapperProps>`
    display: grid;
    align-items: start;

    strong {
        font-weight: ${fonts.weights.normal};
        background: ${(props) =>
            props.highlightColor ? colors.background[props.highlightColor] : 'inherit'};
    }
`

export const ContentWrapper = styled.div`
    display: grid;
    align-content: center;
    gap: ${tokens.spacing20};
    justify-items: stretch;

    ${mq.min('sm')} {
        gap: ${tokens.spacing32};
        justify-items: start;
    }
`

const BulletsGrid = styled.ul`
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    padding: 0;
    margin: 0;
    row-gap: ${tokens.spacing20};
    column-gap: ${tokens.spacing8};

    ${mq.min('xs')} {
        row-gap: ${tokens.spacing20};
        column-gap: ${tokens.spacing24};
    }
`

const BulletItem = styled.li`
    display: grid;
    align-items: center;
    justify-content: center;
    text-align: center;
    row-gap: ${tokens.spacing8};

    ${mq.min('xs')} {
        text-align: initial;
        grid-template-columns: auto 1fr;
        column-gap: ${tokens.spacing12};
    }
`

const BulletImage = styled(Image)`
    max-width: 80px;
    margin: auto;
`

export const contentPlusShortBulletsSliceFragment = graphql`
    fragment ContentPlusShortBulletsSlice on PrismicSlicePageDataBodyContentPlusShortBullets {
        __typename
        primary {
            heading {
                richText
            }
            inverse
            body {
                richText
            }
            highlight_color
        }
        items {
            bullet_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            bullet_text
        }
    }
`
